import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import ErrorAlert from './ErrorAlert'
import LoadingSpinner from './LoadingSpinner'

/**
 * Lo primero que lee un cliente, según por dónde entra. Solo lectura.
 *
 * Los textos vienen de `GET /agente/primeros-mensajes`, que los lee de
 * `shared/primeros_mensajes.py`: el mismo módulo con el que la conversación
 * los manda. Hasta el 13-oct-2026 la pantalla Speech mostraba un saludo
 * escrito acá —«Hola, soy el asistente virtual de PGH…»— que el bot no mandó
 * nunca. Un texto al cliente no se edita desde el portal: cambia en el código,
 * con aprobación de negocio.
 */

interface PrimerMensaje {
  clave: string
  entrada: string
  cuando: string
  texto: string
  botones: string[]
  opciones: string[]
  origen: string
  aprobado: boolean
  nota: string
}

/** `{nombre}` y `{lista}` se pintan como dato variable, no como texto literal. */
function ConVariables({ texto }: { texto: string }) {
  return (
    <>
      {texto.split(/(\{[a-z_]+\})/).map((parte, i) => (/^\{[a-z_]+\}$/.test(parte)
        ? <span key={i} className="rounded bg-brand-light px-1 font-mono text-[12px] text-brand-ink">{parte}</span>
        : <span key={i}>{parte}</span>))}
    </>
  )
}

function Chips({ titulo, items }: { titulo: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <p className="label">{titulo}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((t) => (
          <span key={t} className="rounded-full border border-surface-border bg-white px-3 py-1 text-micro text-ink">
            {t}
          </span>
        ))}
      </div>
    </div>
  )
}

export default function PrimerosMensajes() {
  const [mensajes, setMensajes] = useState<PrimerMensaje[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<{ mensajes: PrimerMensaje[] }>('/agente/primeros-mensajes')
      setMensajes(res.data.mensajes ?? [])
      setError(null)
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron cargar los mensajes del agente.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  if (loading) return <LoadingSpinner label="Cargando los mensajes del agente…" />
  if (error) return <ErrorAlert message={error} onRetry={() => void cargar()} />

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {mensajes.map((m) => (
        <article key={m.clave} className="card flex flex-col gap-3 p-5">
          <header className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-[14px] font-extrabold text-ink">{m.entrada}</h3>
              <p className="mt-0.5 text-micro text-ink-muted">{m.cuando}</p>
            </div>
            <span className={m.aprobado ? 'badge-green shrink-0' : 'badge-amber shrink-0'}>
              {m.aprobado ? 'Wording aprobado' : 'Fuera del catálogo'}
            </span>
          </header>
          <p className="whitespace-pre-wrap rounded-input border border-surface-border bg-surface-alt px-3.5 py-2.5 text-body leading-relaxed text-ink">
            <ConVariables texto={m.texto} />
          </p>
          <Chips titulo="Botones" items={m.botones} />
          <Chips titulo="Opciones de la lista" items={m.opciones} />
          {m.nota && <p className="text-micro text-ink-muted"><ConVariables texto={m.nota} /></p>}
          <p className="mt-auto font-mono text-meta text-ink-faint">{m.origen}</p>
        </article>
      ))}
    </div>
  )
}
